import * as yup from 'yup';

import { Button } from '../../../component/common/button/Button';
import { RowActionsDropdown } from '../../../component/common/table/action-dropdown/RowActionsDropdown';

import { formatCurrency } from '../../../utils/currency/tools';
import { getMenuCategoryColumnDefinition } from '../category/menu-category.schema';

export const getMenuItemsColumnDefinition = (
  onEdit,
  onView,
  onDelete,
  customAction,
  customActionText,
  customActionIcon
) => [
  {
    id: 'imageUrl',
    accessorKey: 'imageUrl',
    size: 56,
    minSize: 56,
    maxSize: 56,
    header: '',
    filterFn: 'tableFilterFn',
    cell: ({ row }) => (
      <img
        src={row.original.imageUrl}
        alt={row.original.name}
        style={{ width: '32px', height: '32px', margin: '6px', borderRadius: '4px' }}
      />
    )
  },
  {
    id: 'name',
    accessorKey: 'name',
    size: 180,
    minSize: 180,
    header: 'Title',
    filterFn: 'tableFilterFn'
  },
  {
    id: 'description',
    accessorKey: 'description',
    size: 180,
    minSize: 180,
    header: 'Description',
    filterFn: 'tableFilterFn',
    cell: ({ row }) => {
      const str = row.original.description;
      if (str.length <= 40) return str;
      return str.slice(0, 40) + '…';
    }
  },
  {
    id: 'price',
    accessorKey: 'price',
    size: 180,
    minSize: 180,
    header: 'Title',
    filterFn: 'tableFilterFn',
    cell: ({ row }) => formatCurrency(row.original.price)
  },
  {
    id: 'actions',
    header: '',
    size: customAction ? 100 : 40,
    minSize: customAction ? 100 : 40,
    maxSize: customAction ? 100 : 40,
    disableExport: true,
    enableSorting: false,
    cell: ({ row }) => {
      if (customAction && typeof customAction === 'function') {
        return (
          <Button onClick={() => customAction(row.original)} small outline>
            {customActionIcon}
            {customActionText}
          </Button>
        );
      }

      const availableActions = [
        onView && { label: 'View', value: 'view', onSelect: onView },
        onEdit && { label: 'Edit', value: 'edit', onSelect: onEdit },
        onDelete && { label: 'Delete', value: 'delete', onSelect: onDelete }
      ].filter(Boolean);

      return <RowActionsDropdown row={row} actions={availableActions} />;
    }
  }
];

export const MENU_ITEMS_TABS = (menus, categories) => [
  {
    label: 'Details',
    fields: [
      {
        type: 'input',
        name: 'name',
        label: 'Name',
        required: true,
        validation: yup.string().required('Title is required')
      },
      {
        type: 'select',
        name: 'menu',
        label: 'Menu',
        options: (Array.isArray(menus) ? menus : []).map((item) => ({
          key: item.id,
          label: item.title,
          value: item,
          active: true
        })),
        required: true,
        validation: yup.object().required('Menu is required')
      },
      {
        type: 'decimal',
        name: 'price',
        label: 'Price',
        required: true,
        validation: yup.number().min(1, 'Number can`t be less than 1').required('Price is required')
      },
      {
        type: 'textarea',
        name: 'description',
        label: 'Description',
        required: true,
        colSpan: 2,
        validation: yup.string().required('Description is required')
      },
      {
        type: 'image',
        name: 'imageUrl',
        label: 'Image',
        colSpan: 2,
        required: true,
        crop: true,
        cropAspect: 4 / 3,
        validation: yup.string().required('Image is required')
      },
      {
        type: 'image',
        name: 'secondaryImageUrl',
        label: 'Secondary Image (optional, shown on hover and in the gallery)',
        colSpan: 2,
        crop: true,
        cropAspect: 4 / 3,
        validation: yup.string().nullable().optional()
      }
    ]
  },
  {
    label: 'Categories',
    fields: [
      {
        type: 'table',
        name: 'categories',
        label: 'Categories',
        columnDefinition: getMenuCategoryColumnDefinition(),
        required: true,
        colSpan: 2,
        entities: categories ?? [],
        validation: yup
          .array()
          .min(1, 'Please select at least one category')
          .required('Categories is required')
      }
    ]
  },
  {
    label: 'Options',
    fields: [
      {
        type: 'option-groups',
        name: 'optionGroups',
        label: 'Option Groups',
        colSpan: 2,
        validation: yup.array().nullable().optional()
      }
    ]
  },
  {
    label: 'Nutritional Information',
    fields: [
      {
        type: 'number',
        name: 'servings',
        label: 'Servings',
        jsonGroup: 'nutrition',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'number',
        name: 'weight',
        label: 'Weight (g)',
        jsonGroup: 'nutrition',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'number',
        name: 'servingSizeG',
        label: 'Serving Size (g)',
        jsonGroup: 'nutrition',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'number',
        name: 'caloriesKcal',
        label: 'Calories (kcal)',
        jsonGroup: 'nutrition',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'number',
        name: 'cholesterolMg',
        label: 'Cholesterol (mg)',
        jsonGroup: 'nutrition',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'number',
        name: 'sodiumMg',
        label: 'Sodium (mg)',
        jsonGroup: 'nutrition',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'empty'
      },

      {
        type: 'label',
        name: 'macros',
        label: 'Macros',
        colSpan: 2
      },
      {
        type: 'decimal',
        name: 'proteinG',
        label: 'Protein (g)',
        jsonGroup: 'nutrition.macros',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'carbsG',
        label: 'Carbohydrates (g)',
        jsonGroup: 'nutrition.macros',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'fatG',
        label: 'Fat (g)',
        jsonGroup: 'nutrition.macros',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'empty'
      },

      {
        type: 'label',
        name: 'fats',
        label: 'Fats',
        colSpan: 2
      },
      {
        type: 'decimal',
        name: 'saturatedG',
        label: 'Saturated (g)',
        jsonGroup: 'nutrition.fats',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'transG',
        label: 'Trans (g)',
        jsonGroup: 'nutrition.fats',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'polyunsaturatedG',
        label: 'Polyunsaturated (g)',
        jsonGroup: 'nutrition.fats',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'monounsaturatedG',
        label: 'Monounsaturated (g)',
        jsonGroup: 'nutrition.fats',
        validation: yup.string().nullable().optional()
      },

      {
        type: 'label',
        name: 'carbohydrates',
        label: 'Carbohydrates',
        colSpan: 2
      },
      {
        type: 'decimal',
        name: 'fiberG',
        label: 'Fiber (g)',
        jsonGroup: 'nutrition.carbohydrates',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'sugarsG',
        label: 'Sugar (g)',
        jsonGroup: 'nutrition.carbohydrates',
        validation: yup.string().nullable().optional()
      },

      {
        type: 'label',
        name: 'micros',
        label: 'Micros',
        colSpan: 2
      },
      {
        type: 'decimal',
        name: 'calciumMg',
        label: 'Calcium (mg)',
        jsonGroup: 'nutrition.micros',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'ironMg',
        label: 'Iron (mg)',
        jsonGroup: 'nutrition.micros',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'potassiumMg',
        label: 'Potassium (mg)',
        jsonGroup: 'nutrition.micros',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'vitaminAMcg',
        label: 'Vitamin A (mcg)',
        jsonGroup: 'nutrition.micros',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'vitaminCMg',
        label: 'Vitamin C (mg)',
        jsonGroup: 'nutrition.micros',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'vitaminDMcg',
        label: 'Vitamin D (mcg)',
        jsonGroup: 'nutrition.micros',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'vitaminBMg',
        label: 'Vitamin B (mg)',
        jsonGroup: 'nutrition.micros',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'magnesiumMg',
        label: 'Magnesium (mg)',
        jsonGroup: 'nutrition.micros',
        validation: yup.string().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'zincMg',
        label: 'Zinc (mg)',
        jsonGroup: 'nutrition.micros',
        validation: yup.string().nullable().optional()
      }
    ]
  }
];
